import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency, formatCurrencyAmount, getDollarValue, ProductTicketStatus } from "@simplyk/common";

import { useLocaleContext } from "../../../common/contexts/LocaleContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip/DashboardChip";
import { DashboardIconBlock } from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Tickets } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface PaymentDetailCardProps {
  title: string;
  index?: number;
  amount: number;
  status?: ProductTicketStatus;
  children?: React.ReactNode;
  /** Hide the price line (default: false) */
  hidePrice?: boolean;
  /** Padding of the card container (default: 2) */
  padding?: number;
  verticalPadding?: number;
  contentGap?: number;
  /** Size of the icon block (default: "medium") */
  iconSize?: "small" | "medium" | "large";
  paymentCurrency: AvailablePaymentCurrency;
  /** Slot for action button (e.g., "+ Note" button) */
  actionSlot?: React.ReactNode;
  /** Custom icon to display (defaults to Tickets) */
  icon?: IconElement;
}

export const PaymentDetailCard: FC<PaymentDetailCardProps> = ({
  title,
  index,
  amount,
  status,
  children,
  hidePrice = false,
  padding = 2,
  verticalPadding = padding,
  contentGap = 1.5,
  iconSize = "medium",
  paymentCurrency,
  actionSlot,
  icon = <Tickets />,
}) => {
  const { locale } = useLocaleContext();
  const { t } = useTranslate();

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.surface.neutral.supershy,
        borderRadius: 1,
        p: padding,
        py: verticalPadding,
        width: "100%",
      })}
      data-test="product-ticket-detail"
    >
      <Stack gap={contentGap}>
        {/* Header row: Icon, Title/Price, Action */}
        <Stack direction="row" gap={2} alignItems={hidePrice ? "center" : "flex-start"} width="100%">
          <DashboardIconBlock vibe="neutral" size={iconSize}>
            {icon}
          </DashboardIconBlock>

          <Stack flex={1} minWidth={0}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Stack minWidth={0}>
                <Stack direction="row" alignItems="baseline" gap={0.5}>
                  <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
                    {title}
                  </DashboardTypography>
                  {index !== undefined && (
                    <DashboardTypography variant="subtitle2" vibe="text-neutral-quiet">
                      #{index}
                    </DashboardTypography>
                  )}
                </Stack>
                {!hidePrice && (
                  <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                    {formatCurrencyAmount({ value: getDollarValue(amount), locale, currency: paymentCurrency })}
                  </DashboardTypography>
                )}
              </Stack>

              {actionSlot}
            </Stack>
          </Stack>
        </Stack>

        {/* Canceled status chip */}
        {status === ProductTicketStatus.Canceled && (
          <DashboardChip
            label={t("dashboard", "orderDetailDrawer.canceled")}
            vibe="danger"
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
          />
        )}

        {/* Children (questions, notes, etc.) */}
        {children}
      </Stack>
    </Box>
  );
};
